import React, { useLayoutEffect, useState, useMemo } from "react";
import { useQuery } from "@apollo/client";
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Stack,
  useTheme,
  useMediaQuery,
  Fade,
  Card,
  CardContent,
  Select,
  MenuItem,
  Skeleton,
  Chip,
} from "@mui/material";
import {
  School as SchoolIcon,
  HolidayVillage as VacationIcon,
  TrendingUp as TrendingUpIcon,
  PauseCircle as PauseIcon,
  Block as BlockIcon,
} from "@mui/icons-material";
import { ProfilePageTeachingsListItem } from "./ProfilePageTeachingsListItem";
import { GET_USER_TEACHINGS } from "../../../apollo-client/queries/teaching-queries";
import { PrimarySpinner } from "../../../components/spinner/primary-spinner";
import { NoResults } from "../no-results";
import { QuestionMarkTooltip } from "../../../components/common/tooltip/question-mark-tooltip";
import { FormattedMessage } from "react-intl";
import { ProfilePageVacationActivationDialog } from "./ProfilePageVacationActivationDialog";
import { useOpenClose } from "../../../hooks/use-open-close";
import { ProfilePageVacationDeactivationDialog } from "./ProfilePageVacationDeactivationDialog";

export const ProfilePageTutoringsBox = () => {
  const [selectedUniversity, setSelectedUniversity] = useState("");
  const [universities, setUniversities] = useState([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const {
    value: isActivateVacationDialogOpen,
    toggle: toggleVacActivationDialog,
  } = useOpenClose(false);
  const {
    value: isDeactivateVacationDialogOpen,
    toggle: toggleVacDeactivationDialog,
  } = useOpenClose(false);

  const {
    data: dataTeachings,
    loading: loadingTeachings,
    error: errorTeachings,
  } = useQuery(GET_USER_TEACHINGS, {
    fetchPolicy: "cache-and-network",
  });

  const toggleVacation = () => {
    dataTeachings && !dataTeachings.user.onHoliday
      ? toggleVacActivationDialog()
      : toggleVacDeactivationDialog();
  };

  //maybe useCallback is better
  /*
    Finds universities from the user's teachings and sets the first university as the selected one
   */
  const handleSetUniversities = () => {
    if (dataTeachings) {
      const activeAndUnactiveUserteachings = [
        ...dataTeachings.user.teachings.teachings,
        ...dataTeachings.user.inactiveTeachings.teachings.filter(
          (teaching) => !teaching.isDeleted
        ),
      ];

      let universityArray = [];
      activeAndUnactiveUserteachings.forEach((teaching) => {
        if (!universityArray.includes(teaching.course.university))
          universityArray.push(teaching.course.university);
      });
      setSelectedUniversity(universityArray[0]);
      setUniversities(universityArray);
    }
  };

  // Check if there are any courses for the selected university
  const hasCoursesForUniversity = useMemo(() => {
    if (!dataTeachings) return false;

    const activeCourses = dataTeachings.user.teachings.teachings.filter(
      (teaching) => teaching.course.university === selectedUniversity
    );
    const inactiveCourses =
      dataTeachings.user.inactiveTeachings.teachings.filter(
        (teaching) =>
          teaching.course.university === selectedUniversity &&
          !teaching.isDeleted
      );

    return activeCourses.length > 0 || inactiveCourses.length > 0;
  }, [dataTeachings, selectedUniversity]);

  // Group courses by status
  const groupedCourses = useMemo(() => {
    if (!dataTeachings) return { active: [], inactive: [], blocked: [] };

    const activeCourses = dataTeachings.user.teachings.teachings.filter(
      (teaching) => teaching.course.university === selectedUniversity
    );
    const inactiveCourses =
      dataTeachings.user.inactiveTeachings.teachings.filter(
        (teaching) =>
          teaching.course.university === selectedUniversity &&
          !teaching.isDeleted
      );

    return {
      active: activeCourses.filter((course) => !course.isBlocked),
      inactive: inactiveCourses.filter((course) => !course.isBlocked),
      blocked: activeCourses.filter((course) => course.isBlocked),
    };
  }, [dataTeachings, selectedUniversity]);

  useLayoutEffect(() => {
    handleSetUniversities();
  }, [dataTeachings]);

  if (loadingTeachings) {
    return (
      <Box
        sx={{
          p: { xs: 1, sm: 2, md: 3 },
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        <Card
          sx={{
            mb: 3,
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Skeleton variant="text" width="60%" height={40} />
            <Skeleton variant="text" width="40%" height={24} sx={{ mt: 1 }} />
          </CardContent>
        </Card>
        <Stack spacing={2}>
          {[1, 2, 3].map((i) => (
            <Card
              key={i}
              sx={{ width: "100%", maxWidth: "100%", boxSizing: "border-box" }}
            >
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Skeleton variant="text" width="80%" height={32} />
                <Skeleton
                  variant="text"
                  width="60%"
                  height={24}
                  sx={{ mt: 1 }}
                />
              </CardContent>
            </Card>
          ))}
        </Stack>
      </Box>
    );
  }

  if (!loadingTeachings && !errorTeachings && universities.length === 0) {
    return (
      <Box
        sx={{
          p: { xs: 1, sm: 2, md: 3 },
          textAlign: "center",
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        <Card
          sx={{
            maxWidth: { xs: "100%", sm: 500 },
            mx: "auto",
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            <SchoolIcon
              sx={{
                fontSize: { xs: 48, sm: 64 },
                color: "text.secondary",
                mb: 2,
              }}
            />
            <Typography
              variant="h5"
              gutterBottom
              color="text.secondary"
              sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
            >
              <FormattedMessage
                id="your-tutoring.no-courses.title"
                defaultMessage="No Tutoring Courses Yet"
              />
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                mb: 3,
                fontSize: { xs: "0.875rem", sm: "1rem" },
                wordBreak: "break-word",
              }}
            >
              <FormattedMessage
                id="your-tutoring.no-courses.description"
                defaultMessage="Start offering tutoring by creating your first course. Students are waiting to learn from you!"
              />
            </Typography>
            <Box sx={{ mt: 3 }}>
              <Chip
                label={
                  <FormattedMessage
                    id="your-tutoring.get-started"
                    defaultMessage="Get Started"
                  />
                }
                color="primary"
                size="large"
                sx={{ px: 3, py: 1 }}
              />
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        p: { xs: 1, sm: 2, md: 3 },
        overflow: "hidden",
        height: "100vh",
      }}
    >
      {!loadingTeachings && !errorTeachings && universities.length > 0 && (
        <>
          {/* Mobile Header */}

          {/* Header with University Selection and Vacation Mode */}
          <Card
            sx={{
              mb: 3,
              width: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
            }}
          >
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={{ xs: 2, md: 3 }}
                alignItems={{ xs: "stretch", md: "center" }}
                justifyContent="space-between"
              >
                {/* University Selection */}
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: { xs: "100%", md: 400 },
                    flex: { xs: "none", md: 1 },
                    minWidth: 0, // Allow shrinking
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    <FormattedMessage
                      id="your-tutoring.university"
                      defaultMessage="University"
                    />
                  </Typography>
                  <Select
                    value={selectedUniversity}
                    onChange={(e) => setSelectedUniversity(e.target.value)}
                    fullWidth
                    variant="outlined"
                    size="small"
                    startAdornment={
                      <SchoolIcon sx={{ mr: 1, color: "text.secondary" }} />
                    }
                    sx={{
                      width: "100%",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(0,0,0,0.23)",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "primary.main",
                      },
                    }}
                  >
                    {universities.map((university) => (
                      <MenuItem key={university} value={university}>
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "100%",
                          }}
                        >
                          {university}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </Box>

                {/* Vacation Mode - Compact */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 0.5, sm: 1 },
                    p: { xs: 1.5, sm: 2 },
                    borderRadius: 1,
                    backgroundColor: dataTeachings?.user.onHoliday
                      ? "#fff5f5"
                      : "transparent",
                    border: dataTeachings?.user.onHoliday
                      ? "1px solid #f86b6b"
                      : "1px solid transparent",
                    transition: "all 0.3s ease-in-out",
                    width: { xs: "100%", md: "auto" },
                    justifyContent: { xs: "space-between", md: "flex-start" },
                    minWidth: 0, // Allow shrinking
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: { xs: 0.5, sm: 1 },
                      minWidth: 0,
                    }}
                  >
                    <VacationIcon
                      color={
                        dataTeachings?.user.onHoliday ? "warning" : "action"
                      }
                      sx={{ fontSize: { xs: 18, sm: 20 }, flexShrink: 0 }}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={
                            dataTeachings && dataTeachings.user.onHoliday
                          }
                          onChange={toggleVacation}
                          size="small"
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: "#f86b6b",
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                              {
                                backgroundColor: "#f86b6b",
                              },
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          sx={{
                            fontSize: { xs: "0.75rem", sm: "0.875rem" },
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            minWidth: 0,
                          }}
                        >
                          <FormattedMessage id="your-tutoring.vacation-mode" />
                        </Typography>
                      }
                      sx={{ margin: 0, minWidth: 0 }}
                    />
                  </Box>
                  <QuestionMarkTooltip tooltipId="your-tutoring.vacation-mode.tooltip" />
                </Box>
              </Stack>

              {/* Vacation Mode Info - Only when active */}
              {dataTeachings && dataTeachings.user.onHoliday && (
                <Fade in={dataTeachings.user.onHoliday}>
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      backgroundColor: "#fef2f2",
                      borderRadius: 1,
                      border: "1px solid #fecaca",
                      width: "100%",
                      boxSizing: "border-box",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#f19191",
                        textAlign: "center",
                        fontWeight: 500,
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        wordBreak: "break-word",
                      }}
                    >
                      <FormattedMessage id="your-tutoring.vacation-mode-chat.info" />
                    </Typography>
                  </Box>
                </Fade>
              )}
            </CardContent>
          </Card>

          {/* Course Content */}
          <Box
            sx={{
              opacity: dataTeachings && dataTeachings.user.onHoliday ? 0.6 : 1,
              transition: "opacity 0.3s ease-in-out",
              width: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
            }}
          >
            {/* Active Courses */}
            {groupedCourses.active.length > 0 && (
              <Box
                sx={{
                  mb: 3,
                  width: "100%",
                  maxWidth: "100%",
                  boxSizing: "border-box",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 2 }}
                >
                  <TrendingUpIcon
                    color="success"
                    sx={{ fontSize: { xs: 20, sm: 24 } }}
                  />
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{
                      fontSize: { xs: "1rem", sm: "1.25rem" },
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      minWidth: 0,
                    }}
                  >
                    <FormattedMessage
                      id="your-tutoring.active-courses"
                      defaultMessage="Active Courses"
                    />
                  </Typography>
                </Stack>
                <Stack spacing={1}>
                  {groupedCourses.active.map((teaching, i) => (
                    <ProfilePageTeachingsListItem
                      key={teaching.course.title + "_active_" + i}
                      title={teaching.course.title}
                      teaching={teaching}
                      user={dataTeachings.user}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {/* Inactive Courses */}
            {groupedCourses.inactive.length > 0 && (
              <Box
                sx={{
                  mb: 3,
                  width: "100%",
                  maxWidth: "100%",
                  boxSizing: "border-box",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 2 }}
                >
                  <PauseIcon
                    color="warning"
                    sx={{ fontSize: { xs: 20, sm: 24 } }}
                  />
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{
                      fontSize: { xs: "1rem", sm: "1.25rem" },
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      minWidth: 0,
                    }}
                  >
                    <FormattedMessage
                      id="your-tutoring.inactive-courses"
                      defaultMessage="Inactive Courses"
                    />
                  </Typography>
                </Stack>
                <Stack spacing={1}>
                  {groupedCourses.inactive.map((teaching, i) => (
                    <ProfilePageTeachingsListItem
                      key={teaching.course.title + "_inactive_" + i}
                      title={teaching.course.title}
                      teaching={teaching}
                      user={dataTeachings.user}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {/* Blocked Courses */}
            {groupedCourses.blocked.length > 0 && (
              <Box
                sx={{
                  mb: 3,
                  width: "100%",
                  maxWidth: "100%",
                  boxSizing: "border-box",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 2 }}
                >
                  <BlockIcon
                    color="error"
                    sx={{ fontSize: { xs: 20, sm: 24 } }}
                  />
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{
                      fontSize: { xs: "1rem", sm: "1.25rem" },
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      minWidth: 0,
                    }}
                  >
                    <FormattedMessage
                      id="your-tutoring.blocked-courses"
                      defaultMessage="Blocked Courses"
                    />
                  </Typography>
                </Stack>
                <Stack spacing={1}>
                  {groupedCourses.blocked.map((teaching, i) => (
                    <ProfilePageTeachingsListItem
                      key={teaching.course.title + "_blocked_" + i}
                      title={teaching.course.title}
                      teaching={teaching}
                      user={dataTeachings.user}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {/* No courses message */}
            {!hasCoursesForUniversity && (
              <Card
                sx={{
                  textAlign: "center",
                  py: 4,
                  width: "100%",
                  maxWidth: "100%",
                  boxSizing: "border-box",
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <SchoolIcon
                    sx={{
                      fontSize: { xs: 40, sm: 48 },
                      color: "text.secondary",
                      mb: 2,
                    }}
                  />
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    gutterBottom
                    sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
                  >
                    <FormattedMessage
                      id="your-tutoring.no-courses-university"
                      defaultMessage="No courses for this university"
                    />
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                  >
                    <FormattedMessage
                      id="your-tutoring.no-courses-university-description"
                      defaultMessage="Try selecting a different university or create new courses."
                    />
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Box>

          {/* Dialogs */}
          <ProfilePageVacationActivationDialog
            isDialogOpen={isActivateVacationDialogOpen}
            toggleDialog={toggleVacActivationDialog}
          />
          <ProfilePageVacationDeactivationDialog
            isDialogOpen={isDeactivateVacationDialogOpen}
            toggleDialog={toggleVacDeactivationDialog}
          />
        </>
      )}
    </Box>
  );
};
